<script lang="ts" setup>
type OptionType = string | { id: string; title: string }

const props = defineProps({
  options: { type: Array as PropType<OptionType[]>, required: true },
  value: { type: String, default: '' },
  name: { type: String, required: true },
  placeholder: { type: String, required: true }
})

const emit = defineEmits(['handleClick'])

// other refs
const search = ref('')
const showOptions = ref(false)
const renderedOptions = ref(props.options)
const mouseIn = ref(false)
const focusedOption = ref(-1)
const headerFocused = ref(false)

const getOptionItemId = (i: number, id: string) => `form-search-option-radio-${i}-${id}`
const getHeaderId = () => `form-search-select-header-${props.name}-${props.placeholder}`

const getId = (option: OptionType) => {
  if (typeof option === 'string') return option
  else return option.id
}

const getTitle = (option: OptionType) => {
  if (typeof option === 'string') return option
  else return option.title
}

// ADD CUSTOM
// const addCustom = true

// SHOW VALUES

const showValue = (val: string) => {
  // if the options are simple stirng, return val
  if (typeof props.options[0] === 'string') return val

  // if not, then get the title
  const opt = props.options.find((opt: any) => {
    return getId(opt) === val
  })

  if (opt) return getTitle(opt)
  else return val
}

// OPTIONS

const clickOptionItem = (opt: any) => {
  // set the model
  emit('handleClick', getId(opt))

  // refocus the header input
  const header = document.getElementById(getHeaderId())
  if (header) header.focus()

  // clear search and re-render options
  focusedOption.value = -1
  search.value = ''
  updateRenderedOptions('')
  clickOutside()
}

const updateSearch = (e: Event) => {
  const searchVal = (e.target as HTMLInputElement).value || ''
  search.value = searchVal
  updateRenderedOptions(searchVal)
}

// UPDATE OPTIONS

const updateRenderedOptions = (search: string) => {
  if (search === '') renderedOptions.value = props.options
  else
    renderedOptions.value = props.options.filter((opt: any) =>
      getTitle(opt).toLowerCase().includes(search.toLowerCase())
    )
}

// KEYBOARD OR MOUSE ACTIONS

const clickHeader = () => {
  onHeaderFocused()
  const header = document.getElementById(getHeaderId())
  if (header) header.focus()
}

const clickOutside = () => {
  mouseIn.value = false
  onHeaderBlur()
}

const onHeaderFocused = () => {
  showOptions.value = true
  headerFocused.value = true
}

const onHeaderBlur = () => {
  search.value = ''

  if (!mouseIn.value) {
    headerFocused.value = false
    showOptions.value = false
    focusedOption.value = -1
    updateRenderedOptions('')
  }
}

const resetVars = () => {
  mouseIn.value = false
  showOptions.value = false
  search.value = ''
  headerFocused.value = false
  focusedOption.value = -1
}

const escHeader = () => {
  resetVars()

  const header = document.getElementById(getHeaderId())
  if (header) header.focus()
}

const clearFocus = () => {
  resetVars()

  renderedOptions.value.forEach((opt, i) => {
    const itemId = getOptionItemId(i, getId(opt))
    const item = document.getElementById(itemId)
    if (item) item.blur()
  })
}

const focusOnItem = (i: number) => {
  mouseIn.value = true
  showOptions.value = true

  const itemId = getOptionItemId(i, getId(renderedOptions.value[i]))
  const item = document.getElementById(itemId)
  if (item) item.focus()
}

const moveUpDown = (isNext: boolean) => {
  const next = focusedOption.value + 1
  const prev = focusedOption.value - 1
  const noNext = focusedOption.value === renderedOptions.value.length - 1 && isNext
  const noPrev = focusedOption.value <= 0 && !isNext

  if (noNext || noPrev) escHeader()
  else if (isNext) {
    focusOnItem(next)
    focusedOption.value = next
  } else {
    focusOnItem(prev)
    focusedOption.value = prev
  }
}

const checkChosen = (opt: any) => {
  return props.value === getId(opt) || props.value === getTitle(opt)
}

watch(
  () => props.options,
  () => {
    renderedOptions.value = props.options
  }
)
</script>

<template>
  <div :class="showOptions ? 'form-select-search-bg' : ''" @click="clickOutside"/>

  <div
    :class="`form-select-search ${showOptions ? 'form-select-search-open' : ''}`"
    @mouseenter="mouseIn = true"
    @mouseleave="mouseIn = false"
  >
    <div :class="`clickable header ${headerFocused ? 'form-select-search-header-focused' : ''}`">
      <input
        :id="getHeaderId()"
        :placeholder="headerFocused ? `Type to search...` : placeholder"
        :value="search"
        @focus="onHeaderFocused"
        @input="updateSearch"
        @blur="onHeaderBlur"
        @keypress.tab="clearFocus"
        @keydown.down="moveUpDown(true)"
        @keydown.up="moveUpDown(false)"
        @keydown.enter.prevent=""
      >

      <div v-if="value" class="header-value" @click="clickHeader">
        <span>{{ showValue(value) }}</span>
      </div>

      <div class="header-chevron">
        <CommonIconChevron />
      </div>

      <div class="header-search">
        <CommonIconSearch />
      </div>
    </div>

    <div class="form-select-search-options clickable">
      <div
        v-for="(option, i) in renderedOptions"
        :key="`form-mult-opt-${name}-${placeholder}-${getId(option)}`"
        :class="`form-select-search-option-item ${checkChosen(option) ? 'chosen' : ''}`"
        @click="clickOptionItem(option)"
      >
        <input
          :id="getOptionItemId(i, getId(option))"
          type="radio"
          :name="`form-multiple-option-name-${getTitle(option)}`"
          :value="getTitle(option)"
          :checked="checkChosen(option)"
          @keydown.enter.prevent="clickOptionItem(option)"
          @keydown.esc.prevent="clearFocus"
          @keydown.down.prevent="moveUpDown(true)"
          @keydown.up.prevent="moveUpDown(false)"
        >

        <div class="form-select-search-option-item-bg"/>

        <CommonIconCheck />

        <span>
          {{ getTitle(option) }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.form-select-search {
  position: relative;

  &-bg {
    position: fixed;
    top: 0;
    right: 0;
    height: 100dvh;
    width: 100dvw;
    z-index: 2;
  }

  .header {
    input {
      transition: padding 0.3s ease-out;
    }

    &-value {
      background-color: white;
      border-radius: 13px;
      position: absolute;
      top: 2px;
      bottom: 2px;
      left: 2px;
      right: 45px;
      padding: 0 20px;
      display: flex;
      align-items: center;
      transition:
        opacity 0.3s ease-out,
        transform 0.3s ease-out;
      text-align: left;

      span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &-search {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 25px;
      display: flex;
      align-items: center;
      pointer-events: none;
      opacity: 0;
      transform: translateX(-10px);
      transition:
        opacity 0.3s ease-out,
        transform 0.3s ease-out;

      svg {
        $searchSize: 15px;
        height: $searchSize;
        width: $searchSize;
        fill: var(--textMedium);
      }
    }
  }

  &-header-focused {
    input {
      padding-left: 55px;
    }

    .header {
      &-search {
        opacity: 1;
        transform: translateX(0);
      }

      &-value {
        opacity: 0;
        transform: translateX(30px);
      }
    }
  }

  &-options {
    display: none;
    animation: appear 0.4s ease-out;
    border: 1px solid gainsboro;
    border-radius: 7px;
    position: absolute;
    width: 100%;
    background: white;
    box-shadow: 0 15px 30px 5px rgba(0, 0, 0, 0.2);
    top: 55px;
    left: 0;
    padding: 10px 0;
    max-height: 200px;
    overflow-y: scroll;
    z-index: 50;
  }

  &-values {
    display: flex;
    flex-wrap: wrap;
    margin: 12px 0 5px;
    padding: 0 5px;
    column-gap: 6px;
    row-gap: 10px;
    align-items: center;
    align-items: center;

    .form-value {
      font-size: 0.85rem;
      padding: 6px 15px;
      border-radius: 30px;
      /* border: 1px solid rgb(233, 233, 233); */
      background-color: var(--mediumPurple);
      display: inline-flex;
      align-items: center;
      gap: 12px;
      transition:
        background 0.3s ease-out,
        color 0.3s ease-out;

      svg {
        $closeSize: 8px;
        height: $closeSize;
        width: $closeSize;
        fill: var(--black);
        transition: fill 0.3s ease-out;
      }
    }
  }
}

.form-select-search-option-item {
  $radioSize: 13px;

  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  position: relative;

  input {
    height: 0;
    width: 0;
    border: none;
    outline: none;
    box-shadow: none;
    position: absolute;
    top: 0;
    left: 0;

    &:focus {
      ~ .form-select-search-option-item {
        &-bg {
          background-color: var(--bgLight);
        }
      }

      ~ span {
        color: var(--purple);
      }
    }
  }

  &-bg {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: -1;
  }

  svg {
    height: $radioSize;
    width: $radioSize;
    fill: transparent;
  }

  span {
    text-align: left;
    display: block;
  }
}

.chosen {
  svg {
    fill: var(--purple);
  }

  span {
    color: var(--purple);
    font-weight: 500;
  }
}

.form-select-search-open {
  .form-select-search {
    &-header-chevron svg {
      transform: rotate(90deg) scaleX(-1);
    }

    &-options {
      display: block;
    }
  }
}

@media (max-width: 600px) {
  .form-select-search {
    &-values {
      margin-top: 14px;
      gap: 8px;
      padding: 0 2px;

      .form-value {
        padding: 10px 17px;
        border-radius: 10px;
      }
    }
  }
}

@media (hover: hover) {
  .form-select-search {
    &-values {
      .form-value:hover {
        background-color: var(--black);
        color: white;

        svg {
          fill: white;
        }
      }
    }
  }

  .form-select-search-option-item:hover {
    background: var(--bgLight);
  }
}

@keyframes appear {
  0% {
    opacity: 0.5;
    transform: translateY(-5px);
  }
  100% {
    opacity: 1;
  }
}
</style>
